/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { trimStart } from 'lodash'

import { getDeploymentResourceProxyRequestsUrl } from '@modules/cloud-api/v1/urls'

import asyncRequest from '../asyncRequests'
import { QUERY_DEPLOYMENT_PROXY, CLEAR_DEPLOYMENT_PROXY_RESPONSE } from '../../constants/actions'
import standaloneAsyncRequest from '../asyncRequests/standalone'

import type { Method } from '../asyncRequests'

const MAX_RESPONSE_SIZE = 10 * 1024 * 1024 // 10MB

export function queryDeploymentProxy({
  deploymentId,
  refId,
  resourceKind,
  method,
  path,
  body,
  meta = {},
  withRedux = true,
}: {
  deploymentId: string
  refId: string
  resourceKind: string
  method: Method
  path: string
  body: any
  meta?: any
  withRedux?: boolean
}) {
  const proxyPath = trimStart(path, `/`)
  const url = getDeploymentResourceProxyRequestsUrl({
    deploymentId,
    resourceKind,
    refId,
    proxyPath,
  })

  /*
   * The Fetch standard doesn't allow GET requests that include a body.
   * As a workaround it needs to:
   *
   * [1]: Make a POST request instead.
   * [2]: Set the X-HTTP-Method-Override header to 'GET'. Adminconsole will
   *      revert it back to GET before forwarding the request to the ES API.
   */
  const isGetMethod = method === `GET`
  const requestMethod = isGetMethod ? `POST` : method // [1]
  const httpMethodOverrideHeader = isGetMethod
    ? {
        'X-HTTP-Method-Override': 'GET', // [2]
      }
    : null

  const headers = {
    'X-Management-Request': true,
    Accept: `*/*`,
    ...httpMethodOverrideHeader,
  }

  // the payload might not be JSON at all, as is the case with `POST /_msearch`
  const parsedBody = parseBody(body)
  const payload = parsedBody === `` ? undefined : parsedBody
  const json = typeof payload !== `string`

  const now = Date.now()

  const responseMapper = (response) => {
    const requestDuration = Date.now() - now

    return {
      requestDuration,
      ...response,
    }
  }

  const requestSettings = {
    json,
    request: {
      headers,
    },
    response: {
      fullStatus: true,
    },
  }

  return withRedux
    ? asyncRequest({
        type: QUERY_DEPLOYMENT_PROXY,
        method: requestMethod, // [1]
        payload,
        url,
        meta: { deploymentId, ...meta },
        crumbs: [deploymentId],
        handleUnauthorized: true,
        requestSettings,
        responseMapper,
        maxResponseSize: MAX_RESPONSE_SIZE,
        maxCompressedResponseSize: MAX_RESPONSE_SIZE,
      })
    : standaloneAsyncRequest({
        method: requestMethod, // [1]
        payload,
        url,
        requestSettings,
        responseMapper,
        maxResponseSize: MAX_RESPONSE_SIZE,
        maxCompressedResponseSize: MAX_RESPONSE_SIZE,
      })
}

export function clearDeploymentProxyResponse(deploymentId: string) {
  return {
    type: CLEAR_DEPLOYMENT_PROXY_RESPONSE,
    meta: { deploymentId },
  }
}

function parseBody(body) {
  if (typeof body !== `string`) {
    return body
  }

  try {
    return JSON.parse(body)
  } catch (e) {
    return body
  }
}
